import { StateCreatorWithDevtools, StoreEffect } from '../store-utils';
import { PriceWarningList, TicketSelectionWarningReport, Warning } from '../../logic/models/ticketselectionwarning';
import { AddTicketsToCartStore } from '../add-tickets-to-cart-store';
import { distributeWarningsOnItems, keepOnlyFirstWarningsOfEachPrice } from '../../logic/ticket-management';
import { useAddPackageToCartStoreExt } from './add-package-to-cart-store-ext';
import { head, uniqBy } from 'lodash';
import { a } from 'msw/lib/glossary-2792c6da';

// INFO: New sale

export interface WarningReportStoreSlice {
    /** The actual warning report that we must care about. */
    currentWarningReport: TicketSelectionWarningReport;
    clearWarningReport: () => void;
    //setCurrentWarningReport: (selectionWarningReport: TicketSelectionWarningReport) => void;

    /** A copy of the last ticket selection warning report received after last ticket change*/
    selectionWarningReport: TicketSelectionWarningReport;
    setSelectionWarningReport: (selectionWarningReport: TicketSelectionWarningReport) => void;

    /** A copy of the last warning report received after an Add To Cart tentative */
    addToCartWarningReport: TicketSelectionWarningReport;
    clearCartWarningReport: () => void;
    setAddToCartWarningReport: (addToCartWarningReport: TicketSelectionWarningReport) => void;

    /** Original warning list by price ID */
    originalWarnings: PriceWarningList[],

    /** Simplified warning by price ID. We show only one at a time in the ticket labels to guide the user. */
    simplifiedWarnings: PriceWarningList[],

    warningsByItemId: { [itemId: string]: Warning[] };

    _recalcWarningsByItemId: () => void;
}

const defaultTicketSelectionWarningReport: TicketSelectionWarningReport = {
    //eventDateId: 0,
    generalWarnings: [],
    priceListWarnings: []
}; 

export const WarningReportStoreSliceDef: StateCreatorWithDevtools<AddTicketsToCartStore & WarningReportStoreSlice, WarningReportStoreSlice> = (set, get) => {
    return {
        currentWarningReport: { ...defaultTicketSelectionWarningReport },

        selectionWarningReport: null,
        addToCartWarningReport: null,
        

        originalWarnings: [],
        simplifiedWarnings: [],
        warningsByItemId: { },

        setSelectionWarningReport: (warningReport: TicketSelectionWarningReport) => {
            set({ selectionWarningReport: warningReport }, false, "setSelectionWarningReport");
            if(warningReport != null)
                set({ currentWarningReport: warningReport }, false, "setSelectionWarningReport/setActualWarningReport");
        },

        setAddToCartWarningReport: (warningReport: TicketSelectionWarningReport) => {
            set({ addToCartWarningReport: warningReport }, false, "setAddToCartWarningReport");
            if(warningReport != null)
                set({ currentWarningReport: warningReport }, false, "setAddToCartWarningReport/setActualWarningReport");
        },

        clearCartWarningReport: () => {
            set({ 
                addToCartWarningReport: null,
                currentWarningReport: get().selectionWarningReport || { ...defaultTicketSelectionWarningReport },
             }, false, "clearCartWarningReport");
        },

        clearWarningReport: () => {
            set({ 
                currentWarningReport: { ...defaultTicketSelectionWarningReport },
                selectionWarningReport: null,
                addToCartWarningReport: null,
            }, false, "clearWarningReport");
        },

        __warningsReportChanged: new StoreEffect<WarningReportStoreSlice>(
            state => state.currentWarningReport, () => get()._recalcWarningsByItemId() ),

        _recalcWarningsByItemId: () => {
            const originalWarnings = get().currentWarningReport?.priceListWarnings;
            if(originalWarnings == null) return;
            
            // Loop to keep only the first warning of each price to keep it simple for the customers
            let simplifiedWarnings = keepOnlyFirstWarningsOfEachPrice(originalWarnings);

            // For each warnings, attribute them to the right item IDs
            // Apply different rules for some non-invasive warnings
            let warningsByItemId = {};

            if(useAddPackageToCartStoreExt.getState().isAPackageSale()) {

                // Get all the warnings in a single list
                let allWarnings = simplifiedWarnings.flatMap(priceWarningList => priceWarningList.warnings);

                if(allWarnings.length > 0) { 
                    // Remove duplicates because we want to show only one warning per price 
                    allWarnings = uniqBy(allWarnings, ['key', 'priceName']);

                    // Create a single price warning list with all warnings with first priceId found
                    // Je prends le premier priceId trouvé car la distibutions des warnings compare avec tous les priceId du package donc le priceId choisi n'a pas d'importance
                    simplifiedWarnings = [{ priceId: head(simplifiedWarnings).priceId, warnings: allWarnings }]; 
                }
                
                warningsByItemId = distributeWarningsOnItems(useAddPackageToCartStoreExt.getState().packageItems, simplifiedWarnings);
            }
            else
                warningsByItemId = distributeWarningsOnItems(get().eventDateItems, simplifiedWarnings);
        
            set({
                originalWarnings: originalWarnings,
                simplifiedWarnings: simplifiedWarnings,
                warningsByItemId: warningsByItemId,
            }, false, "__recalcWarningsByItemId");
            
        },
    };
};

